import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Input,
  Modal,
  Spin,
} from "@arco-design/web-react";
import { IconClose } from "@arco-design/web-react/icon";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { ComponentProps } from "react";

import { normalizeHttpError } from "../../../lib/http/errors";
import {
  copyBudgetVersionFromHistory,
  searchHistoryBudgets,
  fetchBudgetEditor,
} from "../repo/quoteBudgetRepo";
import { formatMoney } from "../service/quoteBudgetEditorViewService";
import {
  beginVersionCopyIntent,
  canonicalVersionCopyScope,
  completeVersionCopyIntent,
  type VersionCopyIntent,
} from "../runtime/versionCopyIntent";
import type { QuoteBudgetHistoryVersion } from "../types";
import { QuoteBudgetDetailPanel } from "./QuoteBudgetDetailPanel";

export type QuoteHistoryDrawerProps = {
  visible: boolean;
  targetBudgetOrderId: string;
  isDirty: boolean;
  hasPendingEntryText?: boolean;
  hasPendingDetailDraft?: boolean;
  hasSaveConflict?: boolean;
  onCancel: () => void;
  onAfterClose?: () => void;
  onLoadVersion: (budgetOrderId: string) => Promise<unknown>;
  onNavigateVersion: (budgetOrderId: string) => void;
  onCopyingChange?: (copying: boolean) => void;
};

type HistoryWorkflowToken = symbol;

const activeHistoryWorkflows = new Map<string, HistoryWorkflowToken>();
const historyWorkflowListeners = new Set<() => void>();
const HISTORY_COPY_INTENT_PREFIX = "flower-master:quote-budget:history-copy";

/** 订阅跨组件实例共享的历史复制工作流状态。 */
function subscribeHistoryWorkflows(listener: () => void) {
  historyWorkflowListeners.add(listener);
  return () => historyWorkflowListeners.delete(listener);
}

/** 通知所有仍挂载的抽屉刷新目标工作流锁。 */
function notifyHistoryWorkflowChange() {
  for (const listener of historyWorkflowListeners) listener();
}

/** 为目标核价单获取唯一工作流令牌，已有流程在途时拒绝重复获取。 */
function acquireHistoryWorkflow(targetBudgetOrderId: string): HistoryWorkflowToken | null {
  if (activeHistoryWorkflows.has(targetBudgetOrderId)) return null;
  const token = Symbol(targetBudgetOrderId);
  activeHistoryWorkflows.set(targetBudgetOrderId, token);
  notifyHistoryWorkflowChange();
  return token;
}

/** 仅允许持有者释放目标锁，避免旧请求结束时误解锁新流程。 */
function releaseHistoryWorkflow(
  targetBudgetOrderId: string,
  token: HistoryWorkflowToken,
) {
  if (activeHistoryWorkflows.get(targetBudgetOrderId) !== token) return;
  activeHistoryWorkflows.delete(targetBudgetOrderId);
  notifyHistoryWorkflowChange();
}

/** 历史版本金额沿用精确格式化，并保持既有的固定两位小数展示。 */
function historyMoneyText(value: string): string {
  const [integer, fraction = ""] = formatMoney(value).split(".");
  return `${integer}.${fraction.padEnd(2, "0")}`;
}

/** 把历史核价接口错误收敛为可操作的中文提示。 */
function historyErrorText(error: unknown, action: "查询" | "复制"): string {
  const normalized = normalizeHttpError(error);
  if (normalized.statusCode === 403) return `当前账号没有${action}历史核价的权限。`;
  if (normalized.code === "QUOTE_BUDGET_HISTORY_SOURCE_NOT_FINALIZED") {
    return "所选历史核价已不再是定稿状态，请重新查询。";
  }
  if (normalized.code === "QUOTE_BUDGET_VERSION_CONFLICT") {
    return "当前核价版本已发生变化，请刷新页面后重试。";
  }
  return `${action}历史核价失败，请稍后重试。`;
}

/** 按课程线路分组查询并复制其他行程的已定稿核价。 */
export function QuoteHistoryDrawer({
  visible,
  targetBudgetOrderId,
  isDirty,
  hasPendingEntryText = false,
  hasPendingDetailDraft = false,
  hasSaveConflict = false,
  onCancel,
  onAfterClose,
  onLoadVersion,
  onNavigateVersion,
  onCopyingChange,
}: QuoteHistoryDrawerProps) {
  const contextRef = useRef({ targetBudgetOrderId, visible, generation: 0 });
  if (
    contextRef.current.targetBudgetOrderId !== targetBudgetOrderId
    || contextRef.current.visible !== visible
  ) {
    contextRef.current = {
      targetBudgetOrderId,
      visible,
      generation: contextRef.current.generation + 1,
    };
  }
  const currentGeneration = contextRef.current.generation;
  const targetWorkflowScope = canonicalVersionCopyScope(targetBudgetOrderId);
  const [title, setTitle] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selected, setSelected] = useState<QuoteBudgetHistoryVersion | null>(null);
  const [previewVersion, setPreviewVersion] = useState<QuoteBudgetHistoryVersion | null>(null);
  const previewQuery = useQuery({
    queryKey: ["quoteBudget", "historyReadOnlyPreview", targetBudgetOrderId, currentGeneration, previewVersion?.budgetOrderId],
    queryFn: () => fetchBudgetEditor(previewVersion!.budgetOrderId),
    enabled: visible && Boolean(previewVersion),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [createdBudgetOrderId, setCreatedBudgetOrderId] = useState<string | null>(null);
  const [createdCopyIntent, setCreatedCopyIntent] = useState<VersionCopyIntent | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [copyAttempted, setCopyAttempted] = useState(false);
  const searchErrorId = useId();
  const onCopyingChangeRef = useRef(onCopyingChange);
  onCopyingChangeRef.current = onCopyingChange;
  const searchMutation = useMutation({
    mutationFn: (request: { targetBudgetOrderId: string; title: string; generation: number }) => (
      searchHistoryBudgets(request.targetBudgetOrderId, request.title)
    ),
  });
  const copyMutation = useMutation({
    mutationFn: (request: {
      targetBudgetOrderId: string;
      sourceBudgetOrderId: string;
      idempotencyKey: string;
      generation: number;
    }) => (
      copyBudgetVersionFromHistory(
        request.targetBudgetOrderId,
        request.sourceBudgetOrderId,
        request.idempotencyKey,
      )
    ),
  });
  const searchBelongsToCurrentTarget = searchMutation.variables?.targetBudgetOrderId
    === targetBudgetOrderId && searchMutation.variables?.generation === currentGeneration;
  const searching = searchMutation.isPending && searchBelongsToCurrentTarget;
  const searched = hasSearched
    && searchBelongsToCurrentTarget
    && searchMutation.variables?.title === title.trim();
  const result = searched ? searchMutation.data : undefined;
  const copyBelongsToCurrentTarget = copyMutation.variables?.targetBudgetOrderId
    === targetBudgetOrderId && copyMutation.variables?.generation === currentGeneration;
  const copying = useSyncExternalStore(
    subscribeHistoryWorkflows,
    () => activeHistoryWorkflows.has(targetWorkflowScope),
    () => false,
  );
  const recoveryLocked = Boolean(loadError && createdBudgetOrderId);
  const controlsDisabled = copying || recoveryLocked;
  const copyError = copyAttempted && copyBelongsToCurrentTarget ? copyMutation.error : null;
  const sourceInvalid = Boolean(copyError
    && normalizeHttpError(copyError).code === "QUOTE_BUDGET_HISTORY_SOURCE_NOT_FINALIZED");
  const searchError = validationError
    ?? (searchMutation.error && searched
      ? historyErrorText(searchMutation.error, "查询")
      : null);

  /** 判断异步结果是否仍属于当前打开的目标核价上下文。 */
  function isCurrentContext(context: { targetBudgetOrderId: string; generation: number }) {
    return contextRef.current.visible
      && contextRef.current.targetBudgetOrderId === context.targetBudgetOrderId
      && contextRef.current.generation === context.generation;
  }

  useEffect(() => {
    onCopyingChange?.(copying);
  }, [copying, onCopyingChange]);

  useEffect(() => () => {
    contextRef.current.generation += 1;
    onCopyingChangeRef.current?.(false);
  }, []);

  useEffect(() => {
    setTitle("");
    setCreatedBudgetOrderId(null);
    setCreatedCopyIntent(null);
    setLoadError(false);
  }, [targetBudgetOrderId]);

  useEffect(() => {
    setValidationError(null);
    setSelected(null);
    setPreviewVersion(null);
    setHasSearched(false);
    setCopyAttempted(false);
  }, [currentGeneration, targetBudgetOrderId, visible]);

  const selectedLabel = useMemo(() => {
    if (!selected) return "";
    return `${selected.budgetOrderNo} / V${selected.versionNo} · ${selected.versionName}`;
  }, [selected]);

  /** 校验线路标题后显式发起历史查询。 */
  function submitSearch() {
    if (controlsDisabled) return;
    const keyword = title.trim();
    if (!keyword) {
      setValidationError("请输入课程线路标题。" );
      return;
    }
    setValidationError(null);
    setSelected(null);
    setCreatedBudgetOrderId(null);
    setLoadError(false);
    setHasSearched(true);
    setCopyAttempted(false);
    searchMutation.mutate({ targetBudgetOrderId, title: keyword, generation: currentGeneration });
  }

  /** 预载新版本成功后再切换路由，失败时保留重试入口。 */
  async function loadAndNavigate(
    budgetOrderId: string,
    context = { targetBudgetOrderId, generation: currentGeneration },
  ): Promise<boolean> {
    if (!isCurrentContext(context)) return false;
    try {
      await onLoadVersion(budgetOrderId);
      if (!isCurrentContext(context)) return false;
      setLoadError(false);
      onNavigateVersion(budgetOrderId);
      return true;
    } catch {
      if (!isCurrentContext(context)) return false;
      setLoadError(true);
      return false;
    }
  }

  /** 创建一次新版本，并把后续失败限定为编辑器预载重试。 */
  async function confirmCopy() {
    if (!selected || isDirty || hasSaveConflict || recoveryLocked || sourceInvalid) return;
    const context = { targetBudgetOrderId, generation: currentGeneration };
    const workflowScope = canonicalVersionCopyScope(context.targetBudgetOrderId);
    const workflowToken = acquireHistoryWorkflow(workflowScope);
    if (!workflowToken) return;
    const sourceBudgetOrderId = selected.budgetOrderId;
    const sourceWorkflowScope = canonicalVersionCopyScope(sourceBudgetOrderId);
    const copyIntent = beginVersionCopyIntent(
      `${HISTORY_COPY_INTENT_PREFIX}:${workflowScope}:${sourceWorkflowScope}`,
      `history-copy:${sourceWorkflowScope}`,
    );
    setCopyAttempted(true);
    try {
      const created = await copyMutation.mutateAsync({
        targetBudgetOrderId,
        sourceBudgetOrderId,
        idempotencyKey: copyIntent.idempotencyKey,
        generation: currentGeneration,
      });
      if (!isCurrentContext(context)) return;
      setSelected(null);
      setCreatedBudgetOrderId(created.budgetOrderId);
      setCreatedCopyIntent(copyIntent);
      if (await loadAndNavigate(created.budgetOrderId, context)) {
        completeVersionCopyIntent(copyIntent);
        setCreatedCopyIntent(null);
      }
    } catch {
      // 复制错误显示在当前确认弹窗；预载错误由 loadAndNavigate 单独处理。
    } finally {
      releaseHistoryWorkflow(workflowScope, workflowToken);
    }
  }

  /** 已创建版本只重试预载，不重复发送非幂等的复制请求。 */
  async function retryLoadCreatedVersion() {
    if (!createdBudgetOrderId || !loadError || isDirty || hasSaveConflict) return;
    const context = { targetBudgetOrderId, generation: currentGeneration };
    const workflowScope = canonicalVersionCopyScope(context.targetBudgetOrderId);
    const workflowToken = acquireHistoryWorkflow(workflowScope);
    if (!workflowToken) return;
    try {
      if (await loadAndNavigate(createdBudgetOrderId, context) && createdCopyIntent) {
        completeVersionCopyIntent(createdCopyIntent);
        setCreatedCopyIntent(null);
      }
    } finally {
      releaseHistoryWorkflow(workflowScope, workflowToken);
    }
  }

  return (
    <>
      <Drawer
        {...({
          role: "dialog",
          "aria-modal": "true",
          "aria-label": "历史核价数据",
        } as unknown as ComponentProps<typeof Drawer>)}
        className="quote-history-drawer"
        title="获取历史数据"
        width={680}
        style={{ maxWidth: "100vw" }}
        visible={visible}
        footer={null}
        closeIcon={(
          <button
            type="button"
            className="quote-history-drawer__close"
            aria-label="关闭历史数据"
            disabled={copying}
          >
            <IconClose />
          </button>
        )}
        unmountOnExit={false}
        onCancel={() => {
          if (!copying) onCancel();
        }}
        afterClose={onAfterClose}
      >
        <div className="quote-history-drawer__search">
          <Input
            value={title}
            maxLength={100}
            placeholder="输入课程线路标题"
            aria-label="输入课程线路标题"
            aria-invalid={Boolean(searchError)}
            aria-describedby={searchError ? searchErrorId : undefined}
            allowClear
            disabled={controlsDisabled}
            onChange={(value) => {
              setTitle(value);
              setValidationError(null);
              setSelected(null);
              setHasSearched(false);
              if (searchMutation.error) {
                searchMutation.reset();
              }
            }}
            onPressEnter={submitSearch}
          />
          <Button type="primary" loading={searching} disabled={controlsDisabled} onClick={submitSearch}>
            查询
          </Button>
        </div>
        <p className="quote-history-drawer__hint">
          多个关键词可用空格分隔，查询结果不包含当前行程。
        </p>
        {searchError ? (
          <div id={searchErrorId}>
            <Alert type={validationError ? "warning" : "error"} content={searchError} showIcon />
          </div>
        ) : null}
        {hasSaveConflict ? (
          <Alert
            type="warning"
            content="核价已被其他用户更新，请先重新加载最新数据后再复制历史核价。"
            showIcon
          />
        ) : isDirty ? (
          <Alert
            type="warning"
            content={hasPendingDetailDraft
              ? "请先确定或取消明细修改，再保存当前核价后继续。"
              : hasPendingEntryText
                ? "快速录入区有未加入内容，请先加入明细或清空后再复制历史数据。"
                : "请先保存当前核价，再复制历史数据。"}
            showIcon
          />
        ) : null}
        {copyError && !selected ? (
          <Alert type="error" content={historyErrorText(copyError, "复制")} showIcon />
        ) : null}
        {loadError && createdBudgetOrderId ? (
          <Alert
            type="warning"
            content={(
              <span>
                新版本已创建，但加载失败。
                <Button
                  type="text"
                  loading={copying}
                  disabled={copying || isDirty || hasSaveConflict}
                  onClick={() => void retryLoadCreatedVersion()}
                >
                  重试加载
                </Button>
              </span>
            )}
            showIcon
          />
        ) : null}
        {result?.limited ? (
          <Alert type="info" content="仅展示最近更新的 20 条课程线路，请补充关键词缩小范围。" />
        ) : null}
        <Spin loading={searching} block>
          <div className="quote-history-drawer__results">
            {!searched ? (
              <Empty description="输入课程线路标题后查询历史核价" />
            ) : result && result.items.length === 0 ? (
              <Empty description="未找到其他行程的已定稿核价" />
            ) : result?.items.map((group) => (
              <section className="quote-history-group" key={group.itineraryId}>
                <header>
                  <div>
                    <strong>{group.itineraryTitle}</strong>
                    <span>{group.startDate ?? "日期未定"} 至 {group.endDate ?? "日期未定"}</span>
                  </div>
                  <span>{group.versions.length} 个定稿版本</span>
                </header>
                <div className="quote-history-group__versions">
                  {group.versions.map((version) => (
                    <article className="quote-history-version" key={version.budgetOrderId}>
                      <div className="quote-history-version__identity">
                        <strong>{version.budgetOrderNo}</strong>
                        <span>V{version.versionNo} · {version.versionName}</span>
                      </div>
                      <b className="quote-history-version__amount">
                        {historyMoneyText(version.costBudgetTotalAmount)}
                      </b>
                      <div className="quote-history-version__actions">
                      <Button
                        aria-label={`预览 ${version.budgetOrderNo} V${version.versionNo}`}
                        disabled={controlsDisabled}
                        onClick={() => setPreviewVersion(version)}
                      >只读预览</Button>
                      <Button
                        className="quote-history-version__action"
                        aria-label={`选择并复制 ${version.budgetOrderNo} V${version.versionNo} ${version.versionName}`}
                        disabled={isDirty || hasSaveConflict || controlsDisabled}
                        onClick={() => {
                          setCopyAttempted(false);
                          setSelected(version);
                        }}
                      >
                        选择并复制
                      </Button>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </Spin>
      </Drawer>
      <Modal title={`历史核价只读预览 · ${previewVersion?.budgetOrderNo ?? ""} V${previewVersion?.versionNo ?? ""}`}
        visible={Boolean(previewVersion) && visible} style={{ width: 980, maxWidth: "calc(100vw - 32px)" }}
        footer={<Button onClick={() => setPreviewVersion(null)}>关闭预览</Button>}
        onCancel={() => setPreviewVersion(null)}>
        <section aria-label="历史核价只读预览">
          {previewQuery.isPending ? <Spin loading /> : previewQuery.isError ? (
            <Alert type="error" content={<span>历史核价预览加载失败，当前未创建新版本。<Button onClick={() => void previewQuery.refetch()}>重试预览</Button></span>} />
          ) : previewQuery.data?.budgetOrder.budgetOrderId !== previewVersion?.budgetOrderId ? (
            <Alert type="error" content="返回的核价与所选版本不一致，请关闭预览并重新查询；未创建新版本。" />
          ) : previewQuery.data ? <>
            <p>{previewQuery.data.itinerarySummary?.title}；学生 {previewQuery.data.budgetOrder.studentCount} 人，老师 {previewQuery.data.budgetOrder.teacherCount} 人，导游 {previewQuery.data.budgetOrder.guideCount} 人，司机 {previewQuery.data.budgetOrder.driverCount} 人。</p>
            <p>历史成本：{formatMoney(previewQuery.data.budgetOrder.costBudgetTotalAmount)} 元。复制到当前行程后，人数联动明细将按当前人数重算。</p>
            <QuoteBudgetDetailPanel lines={previewQuery.data.lines} readonly />
          </> : null}
        </section>
      </Modal>
      <Modal
        title="确认复制历史核价"
        visible={Boolean(selected)}
        okText="确认复制"
        cancelText={sourceInvalid ? "返回查询" : "取消"}
        confirmLoading={copying}
        okButtonProps={{ disabled: copying || isDirty || hasSaveConflict || sourceInvalid }}
        onCancel={() => {
          if (copying) return;
          setSelected(null);
          if (sourceInvalid) {
            setHasSearched(false);
            setCopyAttempted(false);
          }
        }}
        onOk={() => void confirmCopy()}
      >
        {copyError ? (
          <Alert type="error" content={historyErrorText(copyError, "复制")} showIcon />
        ) : null}
        <p>将基于 {selectedLabel} 的定稿数据，在当前行程新建一个草稿版本。</p>
        <p>当前行程已保存的人数、老师是否付费和目标毛利率将保留，人数联动数量会重新计算。</p>
      </Modal>
    </>
  );
}
